import {
  BookOpen,
  Briefcase,
  CalendarCheck,
  Heart,
  History,
  Mail,
  MessageCircle,
  Share2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { GenerationMode, GenerationPhase } from '@/shared/components/navigator-results';

export type CareGuidePanelItem = {
  icon: LucideIcon;
  title: string;
  body: string;
};

export type CareGuideLoadingCopy = {
  titleBuilding: string;
  titleReady: string;
  subtitleBuilding: string;
  subtitleReady: string;
  getItems: CareGuidePanelItem[];
  useItems: CareGuidePanelItem[];
  footerNote: string;
};

export type GenerationStepLabels = Record<GenerationMode, string>;

const sameLabel = (label: string): GenerationStepLabels => ({ 'care-seeker': label, fp: label });

/**
 * Display steps for the progress block, driven by real pipeline signals. The
 * long backend matchmaking window is split into three beats (care-needs /
 * searching / matching); the matching step additionally shows a live
 * "k of N" counter and interpolates the bar between the searching and
 * matching targets by real specialty completions.
 */
export const GENERATION_STEPS: ReadonlyArray<{
  phase: Exclude<GenerationPhase, 'done'>;
  label: GenerationStepLabels;
  target: number;
}> = [
  { phase: 'summary', label: sameLabel('Reading your answers'), target: 18 },
  {
    phase: 'care-needs',
    label: {
      'care-seeker': 'Reviewing your care needs',
      fp: "Reviewing your client's care needs",
    },
    target: 32,
  },
  {
    phase: 'searching',
    label: {
      'care-seeker': 'Searching vetted providers in your area',
      fp: 'Searching vetted providers in their area',
    },
    target: 48,
  },
  { phase: 'matching', label: sameLabel('Matching vetted providers'), target: 80 },
  { phase: 'resources', label: sameLabel('Gathering resources'), target: 88 },
  { phase: 'writing', label: sameLabel('Writing the Care Guide'), target: 100 },
];

const SHARED_SUBTITLES = {
  subtitleBuilding: "This takes about 30 seconds — here's what you'll get and how to use it.",
  subtitleReady: 'Taking you there now…',
  // The spec's email footer was dropped (no ready-notification email exists) —
  // replaced with a neutral reassurance line.
  footerNote: "This usually takes under a minute — we'll take you there the moment it's ready.",
};

export const careGuideLoadingCopy: Record<GenerationMode, CareGuideLoadingCopy> = {
  fp: {
    ...SHARED_SUBTITLES,
    titleBuilding: "Building your client's Care Guide.",
    titleReady: "Your client's Care Guide is ready.",
    getItems: [
      {
        icon: Heart,
        title: "Your client's care stage",
        body: 'A plain-language read on their situation.',
      },
      {
        icon: Briefcase,
        title: 'Matched providers',
        body: 'Top matches by need, with vetted alternates.',
      },
      {
        icon: BookOpen,
        title: 'Resources for each need',
        body: 'Guides and articles picked for the plan.',
      },
    ],
    useItems: [
      { icon: Share2, title: 'Share it', body: 'Send the whole guide to your client as one link.' },
      { icon: Mail, title: 'Refer', body: 'Introduce your client to any match in one click.' },
      { icon: History, title: 'Revisit', body: 'Saved to their file — come back anytime.' },
    ],
  },
  'care-seeker': {
    ...SHARED_SUBTITLES,
    titleBuilding: 'Building your Care Guide.',
    titleReady: 'Your Care Guide is ready.',
    getItems: [
      { icon: Heart, title: 'Your care stage', body: 'A plain-language read on your situation.' },
      {
        icon: Briefcase,
        title: 'Matched providers',
        body: 'Top matches by need, with vetted alternates.',
      },
      {
        icon: BookOpen,
        title: 'Resources for each need',
        body: 'Guides and articles picked for your plan.',
      },
    ],
    useItems: [
      {
        icon: CalendarCheck,
        title: 'Request a consultation',
        body: 'Reach out to any match in one click.',
      },
      {
        icon: MessageCircle,
        title: 'Message providers',
        body: 'Ask questions directly from your guide.',
      },
      {
        icon: History,
        title: 'Revisit anytime',
        body: 'Saved to your care plans — come back whenever you need.',
      },
    ],
  },
};
